// src/Dashboard/pages/user/Inscription/components/AdherentDossierTable.jsx

import React from "react";
import {
  FaCheckCircle,
  FaEye,
  FaFileUpload,
  FaRedoAlt,
  FaUserPlus,
} from "react-icons/fa";

import {
  getAdherentAvatar,
  getStatusMeta,
} from "../utils/inscriptionUtils";

function ProgressBar({ value = 0 }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
      <div
        className="h-full rounded-full bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]"
        style={{
          width: `${Math.max(0, Math.min(100, value))}%`,
        }}
      />
    </div>
  );
}

function ActionButton({ icon, children, onClick, variant = "neutral", visible = true }) {
  if (!visible) return null;

  const variantClass =
    variant === "primary"
      ? "bg-[#013E8E] text-white hover:bg-indigo-800"
      : variant === "renewal"
      ? "bg-orange-600 text-white hover:bg-orange-700"
      : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-bold transition ${variantClass}`}
    >
      {icon}
      {children}
    </button>
  );
}

export default function AdherentDossierTable({
  loading,
  error,
  adherents,
  summaries,
  groupNames,
  activeSeason,
  onOpenDossier,
  onOpenDocuments,
  onOpenRenewal,
}) {
  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="h-28 animate-pulse rounded-[24px] bg-slate-200 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-[24px] border border-red-200 bg-red-50 px-5 py-10 text-center text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
        {error}
      </div>
    );
  }

  if (!adherents.length) {
    return (
      <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 px-5 py-12 text-center dark:border-slate-700 dark:bg-slate-900/40">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-slate-400 shadow-sm ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-700">
          <FaUserPlus className="text-2xl" />
        </div>

        <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-white">
          Aucun adhérent trouvé
        </h3>

        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Aucun dossier ne correspond aux filtres sélectionnés.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="hidden overflow-hidden rounded-[24px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950 lg:block">
        <div className="overflow-x-auto">
          <table className="min-w-[1050px] w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left">Adhérent</th>
                <th className="px-4 py-3 text-left">Statut</th>
                <th className="px-4 py-3 text-left">Saison</th>
                <th className="px-4 py-3 text-left">Groupe</th>
                <th className="px-4 py-3 text-left">Dossier</th>
                <th className="px-4 py-3 text-left">Validation</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {adherents.map((adherent) => {
                const summary = summaries[adherent.id];
                const status = getStatusMeta(adherent.statut_inscription);
                const groups = groupNames[adherent.id] || [];
                const isRenewalNeeded = !summary?.isCurrentSeason;

                return (
                  <tr
                    key={adherent.id}
                    className="bg-white transition hover:bg-slate-50 dark:bg-slate-950 dark:hover:bg-slate-900/70"
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={getAdherentAvatar(adherent)}
                          alt={`${adherent.prenom} ${adherent.nom}`}
                          onError={(event) => {
                            event.currentTarget.src =
                              adherent.genre === "Féminin"
                                ? "/img/judokate_avatar.png"
                                : "/img/judoka_avatar.png";
                          }}
                          className="h-12 w-12 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                        />

                        <div>
                          <p className="font-bold text-slate-900 dark:text-white">
                            {(adherent.nom || "").toUpperCase()}{" "}
                            {adherent.prenom || ""}
                          </p>

                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Licence : {adherent.licence_number || "—"}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${status.className}`}
                      >
                        {status.label}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      <div className="space-y-1">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${
                            summary?.isCurrentSeason
                              ? "bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-800"
                              : "bg-orange-100 text-orange-800 ring-orange-200 dark:bg-orange-900/40 dark:text-orange-200 dark:ring-orange-800"
                          }`}
                        >
                          {adherent.saison_active || "—"}
                        </span>

                        {!summary?.isCurrentSeason && activeSeason && (
                          <p className="text-[11px] font-semibold text-orange-600 dark:text-orange-300">
                            Saison active : {activeSeason}
                          </p>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-4 text-slate-700 dark:text-slate-300">
                      {groups.length ? groups.join(", ") : "Non attribué"}
                    </td>

                    <td className="px-4 py-4">
                      <div className="min-w-[140px] space-y-2">
                        <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                          <span>Documents</span>
                          <span>{summary?.requiredPercent || 0}%</span>
                        </div>

                        <ProgressBar value={summary?.requiredPercent || 0} />
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <div className="min-w-[160px] space-y-2">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${summary?.globalClassName}`}
                        >
                          {summary?.globalLabel}
                        </span>

                        <ProgressBar value={summary?.validationPercent || 0} />
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex flex-wrap justify-end gap-2">
                        <ActionButton
                          variant="primary"
                          icon={<FaEye />}
                          onClick={() => onOpenDossier(adherent)}
                        >
                          Ouvrir
                        </ActionButton>

                        <ActionButton
                          icon={<FaFileUpload />}
                          onClick={() => onOpenDocuments(adherent)}
                        >
                          Documents
                        </ActionButton>

                        <ActionButton
                          variant="renewal"
                          visible={isRenewalNeeded}
                          icon={<FaRedoAlt />}
                          onClick={() => onOpenRenewal(adherent)}
                        >
                          Renouvellement
                        </ActionButton>

                        {!isRenewalNeeded && (
                          <span className="inline-flex items-center gap-1 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:ring-emerald-800">
                            <FaCheckCircle />
                            Saison OK
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-3 lg:hidden">
        {adherents.map((adherent) => {
          const summary = summaries[adherent.id];
          const status = getStatusMeta(adherent.statut_inscription);
          const groups = groupNames[adherent.id] || [];
          const isRenewalNeeded = !summary?.isCurrentSeason;

          return (
            <article
              key={adherent.id}
              className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950"
            >
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <img
                    src={getAdherentAvatar(adherent)}
                    alt={`${adherent.prenom} ${adherent.nom}`}
                    className="h-14 w-14 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                  />

                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-bold text-slate-900 dark:text-white">
                      {(adherent.nom || "").toUpperCase()}{" "}
                      {adherent.prenom || ""}
                    </h3>

                    <div className="mt-2 flex flex-wrap gap-2">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${status.className}`}
                      >
                        {status.label}
                      </span>

                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${summary?.globalClassName}`}
                      >
                        {summary?.globalLabel}
                      </span>
                    </div>

                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      Groupe : {groups.length ? groups.join(", ") : "Non attribué"}
                    </p>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3">
                  <div>
                    <div className="mb-1 flex justify-between text-xs text-slate-500 dark:text-slate-400">
                      <span>Documents</span>
                      <span>{summary?.requiredPercent || 0}%</span>
                    </div>
                    <ProgressBar value={summary?.requiredPercent || 0} />
                  </div>

                  <div>
                    <div className="mb-1 flex justify-between text-xs text-slate-500 dark:text-slate-400">
                      <span>Validation bureau</span>
                      <span>{summary?.validationPercent || 0}%</span>
                    </div>
                    <ProgressBar value={summary?.validationPercent || 0} />
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <ActionButton
                    variant="primary"
                    icon={<FaEye />}
                    onClick={() => onOpenDossier(adherent)}
                  >
                    Ouvrir
                  </ActionButton>

                  <ActionButton
                    icon={<FaFileUpload />}
                    onClick={() => onOpenDocuments(adherent)}
                  >
                    Documents
                  </ActionButton>

                  <ActionButton
                    variant="renewal"
                    visible={isRenewalNeeded}
                    icon={<FaRedoAlt />}
                    onClick={() => onOpenRenewal(adherent)}
                  >
                    Renouvellement
                  </ActionButton>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </>
  );
}